import { ArticlePreviewResponse, Collection, PublicCollectionResponse, PublicUserResponse, UserResponse } from "@authory-packages/core";
import { EmbedTheme } from "../../utils/embeds";
import { UnstyledLink } from "../ReusableStyledComponents/UnstyledLink";
import { V3EmbedAuthor, EmbedWrapper } from "../V3EmbedContent";
import { VerticalSpacing } from "../VerticalSpacing";
import { V3EmbedCollectionTitle, V3EmbedCollBody, V3EmbedCollectionDesc, V3EmbedCollectionSeparator } from "./V3EmbedCollection.styles";
import { V3EmbedContentListItem } from "./V3EmbedContentListitem";
import { V3EmbedCTA } from "./V3EmbedCTA";
import { ItemUrlContext } from "../V3ProfileList/V3ProfileList.utils";

interface V3EmbedCollectionProps {
    embedTheme?: EmbedTheme;
    author: UserResponse | PublicUserResponse;
    showHeader?: boolean;
    collection?: Collection | PublicCollectionResponse;
    items: ArticlePreviewResponse[];
}

export const V3EmbedCollection = ({ embedTheme = EmbedTheme.light, author, collection, showHeader = true, items }: V3EmbedCollectionProps) => {
    const hasDescription = !!collection?.description;
    const colTarget = `${process.env.NEXT_PUBLIC_FRONTEND_URL}/${author.slug}/collection/${collection?.canonicalSlug}`;

    // We leverage the custom background color feature to enforce white/dark background for embeds, which are the only ones supported
    const customBackgroundColor = embedTheme === EmbedTheme.light ? "#FFFFFF" : "#000000";

    return (
        <EmbedWrapper embedTheme={embedTheme}>
            {showHeader && (
                <>
                    <V3EmbedAuthor author={author} embedTheme={embedTheme} customBackgroundColor={customBackgroundColor} />
                    <VerticalSpacing bottom={6}>
                        <UnstyledLink href={colTarget} target="_blank">
                            <V3EmbedCollBody $embedTheme={embedTheme} $numLines={1}>
                                <strong>{`${collection?.articleCount} content items from ${collection?.publicationCount} sources`}</strong>
                            </V3EmbedCollBody>
                        </UnstyledLink>
                    </VerticalSpacing>
                    <VerticalSpacing bottom={hasDescription ? 6 : 16}>
                        <UnstyledLink href={colTarget} target="_blank">
                            <V3EmbedCollectionTitle $embedTheme={embedTheme} $numLines={1} $customBackgroundColor={customBackgroundColor}>
                                {collection?.label}
                            </V3EmbedCollectionTitle>
                        </UnstyledLink>
                    </VerticalSpacing>
                    {hasDescription && (
                        <VerticalSpacing bottom={16}>
                            <V3EmbedCollectionDesc $embedTheme={embedTheme} $numLines={1}>
                                {collection.description}
                            </V3EmbedCollectionDesc>
                        </VerticalSpacing>
                    )}
                    <VerticalSpacing bottom={16}>
                        <V3EmbedCollectionSeparator />
                    </VerticalSpacing>
                </>
            )}
            {items.map((item, index) => (
                <VerticalSpacing bottom={index === items.length - 1 ? 20 : 16} key={index}>
                    <V3EmbedContentListItem
                        item={item}
                        embedTheme={embedTheme}
                        collection={collection}
                        authorSlug={author.slug}
                        context={ItemUrlContext.collection}
                    />
                </VerticalSpacing>
            ))}
            <V3EmbedCTA target={colTarget} label="View entire collection" embedTheme={embedTheme} utmCampaign="collection" />
        </EmbedWrapper>
    );
};
